import type { CashbackDoCliente } from '../../api'
import { descontoETotalDoPedido, maximoDeCashbackNoPedido, type DescontoETotalDoPedido } from '../../lib/cashback-pedido'

/**
 * O cashback no checkout do delivery (spec `docs/specs/cashback-uso-no-cardapio.md` §4.1 e §4.2):
 * o que o bloco mostra e quanto entra no pedido. Só funções puras. Quem consulta o saldo e guarda
 * a escolha do cliente é `use-cashback-no-checkout.ts`.
 */

/** O que o bloco de cashback mostra. */
export const situacoesDoCashback = ['OCULTO', 'CONVITE_PARA_ENTRAR', 'CONVITE_DE_CADASTRO', 'SALDO_ZERO', 'COM_SALDO'] as const

export type SituacaoDoCashback = (typeof situacoesDoCashback)[number]

/**
 * Por que não dá para usar cashback neste pedido, mesmo com saldo:
 * - PAGAMENTO_ONLINE: pagando na entrega daria; é o mínimo do pagamento online que não deixa;
 * - VALOR_DO_PEDIDO: o pedido é pequeno demais, ou o cupom já cobriu os itens.
 */
export const motivosDeNaoUsarCashback = ['PAGAMENTO_ONLINE', 'VALOR_DO_PEDIDO'] as const

export type MotivoDeNaoUsarCashback = (typeof motivosDeNaoUsarCashback)[number]

/** A resposta da consulta do saldo, como o RTK Query entrega. */
export interface ConsultaDoSaldo {
  /** Resposta de `GET /me/cashback`. Ausente enquanto carrega e quando a consulta nem é feita. */
  cashback: CashbackDoCliente | undefined
  comErro: boolean
}

/** Os valores do pedido que está na tela, em reais. */
export interface ValoresDoPedidoNaTela {
  /** Soma dos itens. */
  subtotal: number
  /** Zero quando o pedido não tem cupom. */
  descontoDoCupom: number
  /** Taxa de entrega mais taxa de conveniência. */
  taxas: number
  /** `tipo` da forma de pagamento escolhida. Ausente enquanto o cliente não escolheu. */
  tipoDaFormaDePagamento: string | null | undefined
}

export interface CashbackNoPedido {
  situacao: SituacaoDoCashback
  /** Saldo do cliente nesta loja, em reais. */
  saldo: number
  /** Quanto dos itens a loja aceita em cashback, em %. Zero quer dizer sem teto. */
  percentualMaxUso: number
  /** O máximo que dá para usar neste pedido, em reais. É o valor do switch: o cliente não escolhe outro. */
  maximo: number
  /** Preenchido só quando há saldo e o `maximo` é zero. */
  motivoDeNaoUsar: MotivoDeNaoUsarCashback | null
  /** Quanto entra no pedido, em reais: o `maximo` com o switch ligado, zero com ele desligado. */
  valorUsado: number
  /** O valor é o de um envio que falhou e pode ter debitado (tentativa pendente), e não o que o saldo de agora permite. */
  daTentativaPendente: boolean
}

export interface EntradaDoCashbackNoPedido {
  /** Delivery em loja com `cashbackUsoAtivo` ligado. Mesa, comanda, tablet e totem nunca aceitam. */
  lojaAceitaCashback: boolean
  clienteLogado: boolean
  consulta: ConsultaDoSaldo
  /** O cliente ligou o switch. */
  clienteQuerUsar: boolean
  /** Valor do envio desse cliente que falhou e pode ter debitado (INDISPONIVEL ou sem resposta). Null quando não há. */
  valorPendente: number | null
  pedido: ValoresDoPedidoNaTela
}

/** Abaixo de 1 centavo não há o que usar: o saldo aparece como R$ 0,00. */
const MENOR_SALDO_QUE_DA_PARA_USAR = 0.01

/** Situação e valores do cashback para o pedido que está na tela. */
export function cashbackNoPedido(entrada: EntradaDoCashbackNoPedido): CashbackNoPedido {
  const { lojaAceitaCashback, clienteLogado, consulta, clienteQuerUsar, valorPendente, pedido } = entrada

  if (!lojaAceitaCashback) return semCashbackNoPedido('OCULTO')
  if (!clienteLogado) return semCashbackNoPedido('CONVITE_PARA_ENTRAR')

  // O saldo de agora pode já ter descontado o débito da tentativa: vale o valor dela, mesmo com a consulta falhando.
  // Pedido que mudou tanto que a tentativa não cabe nele (zero): vale a conta de sempre, com o saldo de agora.
  const maximoDaTentativa = maximoDaTentativaPendente(valorPendente, pedido)
  if (maximoDaTentativa > 0) {
    return {
      situacao: 'COM_SALDO',
      saldo: 0,
      percentualMaxUso: 0,
      maximo: maximoDaTentativa,
      motivoDeNaoUsar: null,
      valorUsado: clienteQuerUsar ? maximoDaTentativa : 0,
      daTentativaPendente: true,
    }
  }

  // Carregando, consulta com erro, programa pausado ou cashback fora do ar: nada aparece e o checkout segue.
  const { cashback, comErro } = consulta
  if (comErro) return semCashbackNoPedido('OCULTO')
  if (!cashback) return semCashbackNoPedido('OCULTO')
  if (!cashback.disponivel) return semCashbackNoPedido('OCULTO')

  if (!cashback.encontrado) return semCashbackNoPedido('CONVITE_DE_CADASTRO')

  const saldo = cashback.saldoDisponivel
  const temSaldo = saldo >= MENOR_SALDO_QUE_DA_PARA_USAR
  if (!temSaldo) return semCashbackNoPedido('SALDO_ZERO')

  const limites = {
    saldoDisponivel: saldo,
    percentualMaxUso: cashback.percentualMaxUso,
    totalDosItens: pedido.subtotal,
    descontoDoCupom: pedido.descontoDoCupom,
    taxas: pedido.taxas,
  }
  const maximo = maximoDeCashbackNoPedido({ ...limites, tipoDaFormaDePagamento: pedido.tipoDaFormaDePagamento })

  if (maximo > 0) {
    return {
      situacao: 'COM_SALDO',
      saldo,
      percentualMaxUso: cashback.percentualMaxUso,
      maximo,
      motivoDeNaoUsar: null,
      valorUsado: clienteQuerUsar ? maximo : 0,
      daTentativaPendente: false,
    }
  }

  // Sem forma de pagamento, a conta é a do pagamento na entrega.
  const maximoPagandoNaEntrega = maximoDeCashbackNoPedido({ ...limites, tipoDaFormaDePagamento: null })
  const ehOPagamentoOnlineQueImpede = pedido.tipoDaFormaDePagamento === 'ONLINE' && maximoPagandoNaEntrega > 0

  return {
    situacao: 'COM_SALDO',
    saldo,
    percentualMaxUso: cashback.percentualMaxUso,
    maximo: 0,
    motivoDeNaoUsar: ehOPagamentoOnlineQueImpede ? 'PAGAMENTO_ONLINE' : 'VALOR_DO_PEDIDO',
    valorUsado: 0,
    daTentativaPendente: false,
  }
}

/**
 * O valor da tentativa pendente, sem passar dos itens nem do que precisa sobrar para pagar: o cliente pode ter
 * mexido no carrinho depois da falha. Com o pedido igual, o valor não muda, e o reenvio sai igual. Zero sem tentativa.
 */
function maximoDaTentativaPendente(valorPendente: number | null, pedido: ValoresDoPedidoNaTela): number {
  if (valorPendente === null) return 0

  // O valor da tentativa faz o papel do saldo. O teto da loja fica de fora: quem confere é o débito.
  return maximoDeCashbackNoPedido({
    saldoDisponivel: valorPendente,
    percentualMaxUso: 0,
    totalDosItens: pedido.subtotal,
    descontoDoCupom: pedido.descontoDoCupom,
    taxas: pedido.taxas,
    tipoDaFormaDePagamento: pedido.tipoDaFormaDePagamento,
  })
}

function semCashbackNoPedido(situacao: SituacaoDoCashback): CashbackNoPedido {
  return { situacao, saldo: 0, percentualMaxUso: 0, maximo: 0, motivoDeNaoUsar: null, valorUsado: 0, daTentativaPendente: false }
}

export interface ValoresParaFecharOPedidoNaTela {
  subtotal: number
  taxaEntrega: number
  taxaConveniencia: number
  /** Zero quando o pedido não tem cupom. */
  descontoDoCupom: number
  /** Zero quando o pedido não usa cashback. */
  valorCashback: number
}

/**
 * `desconto` e `total` que a página mostra e manda no corpo do pedido.
 *
 * Sem cashback é a conta que a página sempre fez, sem arredondar: mesa, comanda, tablet, totem e o
 * delivery sem cashback saem com os mesmos números de antes. Com cashback vale a conta em centavos,
 * que é a que o backend refaz antes de debitar.
 */
export function fecharValoresDoPedido(valores: ValoresParaFecharOPedidoNaTela): DescontoETotalDoPedido {
  const usaCashback = valores.valorCashback > 0

  if (!usaCashback) {
    return {
      desconto: valores.descontoDoCupom,
      total: valores.subtotal + valores.taxaEntrega + valores.taxaConveniencia - valores.descontoDoCupom,
    }
  }

  return descontoETotalDoPedido({
    subtotal: valores.subtotal,
    taxas: valores.taxaEntrega + valores.taxaConveniencia,
    descontoDoCupom: valores.descontoDoCupom,
    valorCashback: valores.valorCashback,
  })
}
